/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
import { useState, useEffect } from 'react';

interface UseAnimatedNumberOptions {
  maxDuration?: number; // max duration in ms (default 2000)
  minDuration?: number; // min duration for smallest values (default 300ms)
  delay?: number; // delay before starting animation in ms (default 0)
  enabled?: boolean; // whether animation is enabled (default true)
}

export function useAnimatedNumber(
  finalValue: number,
  options: UseAnimatedNumberOptions = {}
) {
  const { maxDuration = 2000, minDuration = 300, delay = 0, enabled = true } = options;
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setDisplayValue(finalValue);
      return;
    }

    // Calculate duration based on value magnitude
    // Larger values take longer, but cap at maxDuration
    const normalized = Math.min(finalValue / 1000, 1); // normalize to 0-1
    const duration = minDuration + (maxDuration - minDuration) * normalized;

    let startTime: number;
    let animationId: number;
    let timeoutId: number;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function for smooth animation (ease-out-cubic for more obvious effect)
      const easeOutCubic = 1 - (1 - progress) * (1 - progress) * (1 - progress);
      const current = Math.floor(finalValue * easeOutCubic);

      setDisplayValue(current);

      if (progress < 1) {
        animationId = requestAnimationFrame(animate);
      }
    };

    if (delay > 0) {
      timeoutId = window.setTimeout(() => {
        animationId = requestAnimationFrame(animate);
      }, delay);
    } else {
      animationId = requestAnimationFrame(animate);
    }

    return () => {
      cancelAnimationFrame(animationId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [finalValue, maxDuration, minDuration, delay, enabled]);

  return displayValue;
}
